import { useNavigate } from 'react-router'
import { Card } from '@klear/ui/components/card'
import { Skeleton } from '@klear/ui/components/skeleton'
import { Button } from '@klear/ui/components/button'
import { ArrowLeft, MoveUpRight } from 'lucide-react'
import { ROUTE_ORDERS, parseOrderIdFromDisplay } from '@klear/constants'
import { getTraderBaseUrl } from '@klear/runtime-env'
import { useOrder } from '@klear/ui/hooks/use-order'
import { useOrderEventTimelineTree } from '@klear/ui/hooks/use-order-events'
import AdminOrderEventItems from '@/app/order/AdminOrderEventItems'
import OrderContextMeta from '@/app/order/OrderContextMeta'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

interface OrderDetailProps {
  appPrefix: string
  orderIdDisplay: string
}

export default function OrderDetail({ appPrefix, orderIdDisplay }: OrderDetailProps) {
  const navigate = useNavigate()
  const parsedOrderId = parseOrderIdFromDisplay(orderIdDisplay)
  const orderId = parsedOrderId != null && parsedOrderId > 0 ? parsedOrderId : null
  const { order, isLoading: isOrderLoading } = useOrder(orderId ?? 0, { enabled: orderId != null })
  const { events, isLoading: isTreeLoading } = useOrderEventTimelineTree(orderId ?? undefined)
  useDocumentTitle(order?.idDisplay, 'Order', events[0]?.envelope.traderName)

  if (orderId != null && (isOrderLoading || isTreeLoading)) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card className="p-6">
          <Skeleton className="h-[200px]" />
        </Card>
      </div>
    )
  }

  if (orderId == null || !order) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Card className="p-6">
          <p className="text-muted-foreground">Order not found</p>
        </Card>
      </div>
    )
  }

  const traderId = order.traderId ?? events[0]?.envelope.traderId ?? 0

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Order Details</h1>
          <OrderContextMeta
            idDisplay={order.idDisplay ?? orderIdDisplay}
            customerName={order.customerOrganizationName}
            traderId={traderId}
            traderName={events[0]?.envelope.traderName}
          />
        </div>
        <div className="flex items-center gap-2">
          {traderId > 0 ? (
            <Button variant="outline" asChild>
              <a href={`${getTraderBaseUrl()}/${traderId}/${ROUTE_ORDERS}/${orderId}`}>
                <MoveUpRight className="mr-2 h-4 w-4" />
                View in Trader app
              </a>
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      {events.length === 0 ? (
        <Card className="p-6">
          <p className="text-muted-foreground">No events on this order yet</p>
        </Card>
      ) : (
        <AdminOrderEventItems
          events={events}
          traderId={traderId}
          orderId={orderId}
          timelineEvents={events}
          appPrefix={appPrefix}
        />
      )}
    </div>
  )
}
